/**
 * F2-c: pagina dettaglio/gestione hotel (Area Proprietario).
 *
 * L'owner apre un hotel dalla dashboard e ne gestisce, da remoto (cloud):
 *  - Anagrafica + metodo d'accesso + feature flags
 *  - Camere (CRUD + disattiva)
 *  - Listini/prezzi per tipologia
 *  - Utenti operatori + reset PIN
 *
 * Chrome owner dedicato (come OwnerDashboardPage), tab navigabili, mobile-first.
 * useQuery per il detail; le sezioni fanno mutation + invalidate sulla query.
 */
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@hotel-manager/ui/components';
import {
  BedDouble,
  Building2,
  ChevronLeft,
  type LucideIcon,
  RefreshCw,
  Settings2,
  Users,
  Wallet,
} from '@hotel-manager/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchHotelDetail } from '../../api/owner.js';
import { OwnerLogoutButton } from './OwnerLogoutButton.js';
import { AnagraficaSection } from './sections/AnagraficaSection.js';
import { CamereSection } from './sections/CamereSection.js';
import { ListiniSection } from './sections/ListiniSection.js';
import { UtentiSection } from './sections/UtentiSection.js';

type TabId = 'anagrafica' | 'camere' | 'listini' | 'utenti';

const TABS: Array<{ id: TabId; label: string; icon: LucideIcon }> = [
  { id: 'anagrafica', label: 'Anagrafica', icon: Settings2 },
  { id: 'camere', label: 'Camere', icon: BedDouble },
  { id: 'listini', label: 'Listini', icon: Wallet },
  { id: 'utenti', label: 'Utenti', icon: Users },
];

export function OwnerHotelDetailPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const [active, setActive] = useState<TabId>('anagrafica');

  const query = useQuery({
    queryKey: ['owner', 'hotel', tenantId],
    queryFn: () => fetchHotelDetail(tenantId),
    enabled: tenantId.length > 0,
  });

  const detail = query.data;

  return (
    <main className="min-h-screen bg-bg-base text-text-primary font-sans antialiased">
      {/* Topbar owner */}
      <header className="sticky top-0 z-10 bg-bg-topbar/95 backdrop-blur border-b border-border-base">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Link
              to="/owner"
              aria-label="Torna agli hotel"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border-base bg-bg-card text-text-secondary hover:bg-bg-muted transition-colors shrink-0"
            >
              <ChevronLeft size={18} strokeWidth={2} />
            </Link>
            <div className="min-w-0">
              <div className="text-sm font-bold leading-tight tracking-hm-tight truncate flex items-center gap-1.5">
                <Building2 size={15} strokeWidth={1.75} className="text-accent-primary shrink-0" />
                {detail?.tenant.name ?? 'Hotel'}
              </div>
              {detail && (
                <div className="text-[11px] text-text-muted truncate">{detail.tenant.slug}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Aggiorna"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-base bg-bg-card px-3 h-9 text-[13px] font-medium text-text-secondary hover:bg-bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                strokeWidth={1.75}
                className={query.isFetching ? 'animate-spin' : ''}
              />
              <span className="hidden sm:inline">Aggiorna</span>
            </button>
            <OwnerLogoutButton />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-5">
          <h1 className="text-2xl font-bold tracking-hm-tight">Gestione hotel</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Configura anagrafica, camere, listini e operatori · da remoto
          </p>
        </div>

        {query.isPending && <DetailSkeleton />}

        {query.isError && !detail && (
          <div
            role="alert"
            className="rounded-2xl border border-border-base bg-bg-card p-8 text-center shadow-hm-sm"
          >
            <p className="text-text-primary font-semibold">Impossibile caricare l'hotel.</p>
            <p className="text-sm text-text-secondary mt-1">Controlla la connessione e riprova.</p>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors"
            >
              <RefreshCw size={14} strokeWidth={1.75} />
              Riprova
            </button>
          </div>
        )}

        {detail && (
          <Tabs value={active} onValueChange={(v) => setActive(v as TabId)}>
            <TabsList className="border-b border-border-base mb-6 overflow-x-auto">
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id} data-testid={`owner-hotel-tab-${t.id}`}>
                  <t.icon size={17} strokeWidth={1.75} />
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="anagrafica">
              {/*
                Le sezioni Anagrafica/Listini seedano lo stato editabile dai
                props una sola volta: senza una key che cambia al refetch (o al
                cambio hotel) mostrerebbero dati stale dopo un salvataggio. La
                key combina il tenant.id con query.dataUpdatedAt, che cambia ad
                ogni fetch riuscito → remount con props freschi.
              */}
              <AnagraficaSection
                key={`anag-${detail.tenant.id}-${query.dataUpdatedAt}`}
                tenantId={tenantId}
                tenant={detail.tenant}
              />
            </TabsContent>
            <TabsContent value="camere">
              <CamereSection
                tenantId={tenantId}
                rooms={detail.rooms}
                roomTypes={detail.roomTypes}
              />
            </TabsContent>
            <TabsContent value="listini">
              <ListiniSection
                key={`listini-${detail.tenant.id}-${query.dataUpdatedAt}`}
                tenantId={tenantId}
                roomTypes={detail.roomTypes}
                pricingRules={detail.pricingRules}
              />
            </TabsContent>
            <TabsContent value="utenti">
              <UtentiSection tenantId={tenantId} users={detail.users} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </main>
  );
}

function DetailSkeleton() {
  return (
    <div data-testid="hotel-detail-skeleton" className="animate-pulse">
      <div className="h-10 w-full max-w-md rounded bg-bg-muted mb-6" />
      <div className="rounded-2xl border border-border-base bg-bg-card shadow-hm-sm p-6 space-y-4">
        <div className="h-5 w-1/3 rounded bg-bg-muted" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-11 w-full rounded-lg bg-bg-muted" />
          ))}
        </div>
      </div>
    </div>
  );
}
